import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Notification from 'components/notification';
import {
  CUSTOM_CONTENT_METRICS_KEYS,
  ICustomContentMetric,
  ICustomContentMetricsApiResponse,
  ICustomContentMetricsResponse,
  getCustomContentMetrics,
} from 'services/customContent';
import { normalizeCustomContentType } from 'pages/CustomContent/utils';

interface IUseCustomContentMetricsParams {
  enabled?: boolean;
  customContentIds?: string[];
}

interface IUseCustomContentMetricsResult {
  metricsById: ICustomContentMetricsResponse;
  certificationNameByContentId: Record<string, string>;
  metricsLoading: boolean;
  metricsError: boolean;
  refreshMetrics: () => Promise<ICustomContentMetricsResponse>;
}

const EMPTY_METRICS: ICustomContentMetricsResponse = {};
const EMPTY_CERTIFICATIONS: Record<string, string> = {};

const METRICS_BY_CONTENT_TYPE: Record<string, Array<{ key: string; label: string }>> = {
  course: [
    { key: CUSTOM_CONTENT_METRICS_KEYS.ENGAGED_USERS, label: 'Engaged Users' },
    { key: CUSTOM_CONTENT_METRICS_KEYS.COMPLETION_RATE, label: 'Completion Rate' },
    { key: CUSTOM_CONTENT_METRICS_KEYS.AVG_PROGRESS, label: 'Avg Progress' },
  ],
  standard_lab: [
    { key: CUSTOM_CONTENT_METRICS_KEYS.ENGAGED_USERS, label: 'Engaged Users' },
    { key: CUSTOM_CONTENT_METRICS_KEYS.TOTAL_STARTS, label: 'Total Starts' },
    { key: CUSTOM_CONTENT_METRICS_KEYS.AVG_SESSION_DURATION, label: 'Avg Session Duration' },
  ],
  ctf_lab: [
    { key: CUSTOM_CONTENT_METRICS_KEYS.ENGAGED_USERS, label: 'Engaged Users' },
    { key: CUSTOM_CONTENT_METRICS_KEYS.TOTAL_STARTS, label: 'Total Starts' },
    { key: CUSTOM_CONTENT_METRICS_KEYS.AVG_SESSION_DURATION, label: 'Avg Session Duration' },
    { key: CUSTOM_CONTENT_METRICS_KEYS.AVG_FLAGS_CAPTURED, label: 'Avg Flags Captured' },
    { key: CUSTOM_CONTENT_METRICS_KEYS.COMPLETION_RATE, label: 'Completion Rate' },
  ],
  assessment: [
    { key: CUSTOM_CONTENT_METRICS_KEYS.AVG_SCORE, label: 'Avg Score' },
    { key: CUSTOM_CONTENT_METRICS_KEYS.ATTEMPTS, label: 'Attempts' },
    { key: CUSTOM_CONTENT_METRICS_KEYS.PASS_RATE, label: 'Pass Rate' },
    { key: CUSTOM_CONTENT_METRICS_KEYS.IMPROVEMENT_RATE, label: 'Improvement Rate' },
  ],
  learning_path: [
    { key: CUSTOM_CONTENT_METRICS_KEYS.ENGAGED_USERS, label: 'Engaged Users' },
    { key: CUSTOM_CONTENT_METRICS_KEYS.COMPLETION_RATE, label: 'Completion Rate' },
    { key: CUSTOM_CONTENT_METRICS_KEYS.AVG_PROGRESS, label: 'Avg Progress' },
  ],
  learning_path_with_cert: [
    { key: CUSTOM_CONTENT_METRICS_KEYS.ENGAGED_USERS, label: 'Engaged Users' },
    { key: CUSTOM_CONTENT_METRICS_KEYS.COMPLETION_RATE, label: 'Completion Rate' },
    { key: CUSTOM_CONTENT_METRICS_KEYS.AVG_PROGRESS, label: 'Avg Progress' },
    {
      key: CUSTOM_CONTENT_METRICS_KEYS.EXAM_PARTICIPATION_RATE,
      label: 'Exam Participation Rate',
    },
    { key: CUSTOM_CONTENT_METRICS_KEYS.EXAM_PASS_RATE, label: 'Exam Pass Rate' },
  ],
};

const normalizeMetrics = (
  data: ICustomContentMetricsApiResponse
): {
  metricsById: ICustomContentMetricsResponse;
  certificationNameByContentId: Record<string, string>;
} => {
  if (!Array.isArray(data)) {
    return { metricsById: EMPTY_METRICS, certificationNameByContentId: EMPTY_CERTIFICATIONS };
  }

  return data.reduce(
    (acc, item) => {
      const contentId = typeof item?.content_id === 'string' ? item.content_id : '';
      const contentType = normalizeCustomContentType(item?.content_type);

      if (!contentId || !contentType) {
        return acc;
      }

      const metricDefs = METRICS_BY_CONTENT_TYPE[contentType] || [];
      const metrics = metricDefs
        .map<ICustomContentMetric | null>(({ key, label }) => {
          const value = item[key];
          if (value === null || value === undefined || value === '') return null;
          return {
            key,
            label,
            value: typeof value === 'string' ? value : String(value),
          };
        })
        .filter((metric): metric is ICustomContentMetric => Boolean(metric));

      acc.metricsById[contentId] = metrics;

      if (
        contentType === 'learning_path_with_cert' &&
        typeof item.certification_name === 'string'
      ) {
        acc.certificationNameByContentId[contentId] = item.certification_name;
      }

      return acc;
    },
    {
      metricsById: {} as ICustomContentMetricsResponse,
      certificationNameByContentId: {} as Record<string, string>,
    }
  );
};

const useCustomContentMetrics = ({
  enabled = true,
  customContentIds = [],
}: IUseCustomContentMetricsParams): IUseCustomContentMetricsResult => {
  const [metricsById, setMetricsById] = useState<ICustomContentMetricsResponse>(EMPTY_METRICS);
  const [certificationNameByContentId, setCertificationNameByContentId] =
    useState<Record<string, string>>(EMPTY_CERTIFICATIONS);
  const [metricsLoading, setMetricsLoading] = useState(false);
  const [metricsError, setMetricsError] = useState(false);

  const normalizedIds = useMemo(
    () => Array.from(new Set(customContentIds.filter(Boolean))),
    [customContentIds]
  );
  const requestSignature = useMemo(() => {
    if (!enabled || normalizedIds.length === 0) return '';
    return normalizedIds.join(',');
  }, [enabled, normalizedIds]);
  const lastFetchedSignatureRef = useRef('');
  const inFlightSignatureRef = useRef('');
  const inFlightPromiseRef = useRef<Promise<ICustomContentMetricsResponse> | null>(null);
  const latestRequestSignatureRef = useRef(requestSignature);

  useEffect(() => {
    latestRequestSignatureRef.current = requestSignature;
  }, [requestSignature]);

  const refreshMetrics = useCallback(async () => {
    const requestSignatureAtStart = requestSignature;

    if (!requestSignatureAtStart) {
      setMetricsById(EMPTY_METRICS);
      setCertificationNameByContentId(EMPTY_CERTIFICATIONS);
      setMetricsError(false);
      setMetricsLoading(false);
      lastFetchedSignatureRef.current = '';
      return EMPTY_METRICS;
    }

    if (lastFetchedSignatureRef.current === requestSignatureAtStart) {
      return metricsById;
    }

    if (
      inFlightPromiseRef.current &&
      inFlightSignatureRef.current &&
      inFlightSignatureRef.current === requestSignatureAtStart
    ) {
      return inFlightPromiseRef.current;
    }

    setMetricsLoading(true);
    setMetricsError(false);

    const requestPromise = (async () => {
      try {
        const { data } = await getCustomContentMetrics({
          custom_content_ids: normalizedIds,
        });
        const normalizedData = normalizeMetrics(data);

        if (latestRequestSignatureRef.current !== requestSignatureAtStart) {
          return EMPTY_METRICS;
        }

        setMetricsById(normalizedData.metricsById);
        setCertificationNameByContentId(normalizedData.certificationNameByContentId);
        lastFetchedSignatureRef.current = requestSignatureAtStart;
        return normalizedData.metricsById;
      } catch (_error) {
        if (latestRequestSignatureRef.current !== requestSignatureAtStart) {
          return EMPTY_METRICS;
        }

        setMetricsError(true);
        Notification({
          text: 'There has been a problem fetching custom content metrics',
          type: 'error',
        });
        setMetricsById(EMPTY_METRICS);
        setCertificationNameByContentId(EMPTY_CERTIFICATIONS);
        return EMPTY_METRICS;
      } finally {
        if (inFlightPromiseRef.current === requestPromise) {
          inFlightPromiseRef.current = null;
          inFlightSignatureRef.current = '';
        }

        if (latestRequestSignatureRef.current === requestSignatureAtStart) {
          setMetricsLoading(false);
        }
      }
    })();

    inFlightPromiseRef.current = requestPromise;
    inFlightSignatureRef.current = requestSignatureAtStart;
    return requestPromise;
  }, [metricsById, normalizedIds, requestSignature]);

  useEffect(() => {
    refreshMetrics();
  }, [refreshMetrics]);

  return {
    metricsById,
    certificationNameByContentId,
    metricsLoading,
    metricsError,
    refreshMetrics,
  };
};

export default useCustomContentMetrics;
